import React, { useEffect } from 'react';
import { Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PassOrderPageData } from '../../api/models/PassOrderPageData';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import PassScheduleSelector from '../../shared/passScheduleSelector/PassScheduleSelector';
import { dropUnavailableSelections } from '../../shared/passScheduleSelector/utils';
import { usePassAvailability } from '../../shared/passScheduleSelector/usePassAvailability';
import { isFixedScheduleMonthly } from '../../managePassesPage/utils/passUtils';

type Props = {
  setShouldDisableNext: (v: boolean) => void;
};

const PassScheduleStep = ({ setShouldDisableNext }: Props) => {
  const { t } = useTranslation();
  const { orderPageState, setOrderPageState } = useOrderPage();
  const passOrderPage = orderPageState.orderPage as unknown as PassOrderPageData;
  const { account, passScheduleSelections, specificOrderPage } = orderPageState;
  const troubleshoot = specificOrderPage?.troubleshoot === true;

  const weeklySessionCount = passOrderPage.weekly_session_count;

  const {
    availability,
    loading: availabilityLoading,
    error: availabilityError,
  } = usePassAvailability(passOrderPage, account);

  useEffect(() => {
    if (!availability) return;
    // Slots the customer already picked can fill up while they are further along the flow (or
    // while the server rejects their purchase for capacity). Drop those so the count check
    // blocks Next until they have picked again.
    const stillAvailable = dropUnavailableSelections(passScheduleSelections, availability);
    if (stillAvailable.length !== passScheduleSelections.length) {
      setOrderPageState({ ...orderPageState, passScheduleSelections: stillAvailable });
    }
  }, [availability]);

  useEffect(() => {
    const countDisabled =
      weeklySessionCount != null
        ? passScheduleSelections.length !== weeklySessionCount
        : passScheduleSelections.length === 0;
    setShouldDisableNext(countDisabled || availabilityLoading || availabilityError);
    return () => setShouldDisableNext(false);
  }, [passScheduleSelections.length, setShouldDisableNext, weeklySessionCount, availabilityLoading, availabilityError]);

  return (
    <Grid container flexDirection="column" gap={1.5}>
      <OrderPageTypography variant="h3" bold>
        {t('pass_schedule.heading')}
      </OrderPageTypography>
      {!isFixedScheduleMonthly(passOrderPage.pass_type) && (
        <>
          <OrderPageTypography variant="body2">{t('pass_schedule.subheading')}</OrderPageTypography>
          <OrderPageTypography variant="body2">{t('pass_schedule.changeableLater')}</OrderPageTypography>
        </>
      )}

      {availabilityError && <Alert severity="error">{t('pass_schedule.errors.availabilityLoadFailed')}</Alert>}
      <PassScheduleSelector
        passOrderPage={passOrderPage}
        account={account}
        value={passScheduleSelections}
        onChange={(next) => setOrderPageState({ ...orderPageState, passScheduleSelections: next })}
        maxSelections={weeklySessionCount ?? undefined}
        availability={availability}
        troubleshoot={troubleshoot}
      />

      {weeklySessionCount != null ? (
        <OrderPageTypography variant="body2" color="secondary">
          {t('pass_schedule.counter', {
            selected: passScheduleSelections.length,
            required: weeklySessionCount,
          })}
        </OrderPageTypography>
      ) : (
        passScheduleSelections.length === 0 && (
          <OrderPageTypography variant="body2" color="secondary">
            {t('pass_schedule.selectAtLeastOne')}
          </OrderPageTypography>
        )
      )}
      {weeklySessionCount != null && passScheduleSelections.length !== weeklySessionCount && (
        <OrderPageTypography variant="body2" color="secondary">
          {t('pass_schedule.exactCountRequired', { count: weeklySessionCount })}
        </OrderPageTypography>
      )}
    </Grid>
  );
};

export default PassScheduleStep;
